import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import CommonSelect from "../../../core/common/commonSelect";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import api from "../../../services/api";
import DeleteModal from "../../../core/modals/deleteModal";
import Loader from "../../../core/common/loader";
import { truncateCompanyText } from "../../../helper/company_helper";
import { all_routes } from "../../router/all_routes";
import AddCategory from "./components/AddCategory";
import AddSubCategory from "./components/AddSubCategory";
import CategoryTable from "./components/CategoryTable";
import EditCategory from "./components/EditCategory";
import { notify } from "../../../services/toast.config";

// Initial form data for company selection
const INITIAL_COMPANY_DATA = {
  company_name: "Select Company",
  company_id: "",
  company_type: "Type",
};

const AssetsCategory = () => {
  const signature = localStorage.getItem("signature");

  // Data states
  const [data, setData] = useState<any[]>([]);
  const [filteredData, setFilteredData] = useState(data);
  const [companies, setCompanies] = useState<any[]>([]);
  const [formDataCompanyChoose, setformDataCompanyChoose] =
    useState(INITIAL_COMPANY_DATA);

  // Filter states
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [selectedStartDate, setSelectedStartDate] =
    useState<moment.Moment | null>(null);
  const [selectedEndDate, setSelectedEndDate] = useState<moment.Moment | null>(
    null
  );

  // UI states
  const [isLoading, setIsLoading] = useState(false);
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);

  // Modal states
  const [selectedCategoryEdit, setSelectedCategoryEdit] = useState<any>({});
  const [selectedParentCategory, setSelectedParentCategory] = useState<any>({});
  const [deleteRecord, setDeleteRecord] = useState<{
    id: number;
    name: string;
  } | null>(null);

  // Handle category edit selection
  const handleCategoryEdit = (record: any) => {
    setSelectedCategoryEdit(record);
  };

  // Delete single category
  const handleDelete = async () => {
    if (!deleteRecord) return;

    try {
      setIsLoading(true);
      const response = await api.post("userService/deleteCategory", {
        signature,
        id: deleteRecord.id,
      });
      notify.success(
        response?.data?.message[0]?.msg || "Category deleted successfully."
      );
    } catch (error: any) {
      notify.error(error.data?.message[0]?.msg || "An error occurred.");
    } finally {
      await updateUI();
      setIsLoading(false);
      setDeleteRecord(null);
    }
  };

  // Fetch companies list
  const getAllListsAndData = async () => {
    try {
      const response = await api.post("userService/getAllCompanyList", {
        signature,
      });
      setCompanies(response?.data?.data?.data || []);
    } catch (error) {
      notify.error("Failed to load companies.");
    }
  };

  // Fetch categories data
  const getAllCategoriesData = async () => {
    if (!formDataCompanyChoose.company_id) return;

    try {
      const response = await api.post("userService/getCategory", {
        signature,
        company_id: parseInt(formDataCompanyChoose.company_id),
        company_type: formDataCompanyChoose.company_type,
      });
      const updatedData = response?.data?.data?.data || [];
      setData(updatedData);
      setFilteredData(updatedData);
    } catch (error) {
      notify.error("Failed to load categories.");
    }
  };

  // Update UI with loading delay
  const updateUI = async () => {
    setIsLoading(true);
    const startTime = Date.now();

    try {
      setExpandedRowKeys([]);
      await getAllCategoriesData();
    } catch (error) {
    } finally {
      const elapsed = Date.now() - startTime;
      const remaining = 300 - elapsed;

      if (remaining > 0) {
        setTimeout(() => setIsLoading(false), remaining);
      } else {
        setIsLoading(false);
      }
    }
  };

  // Handle company selection change
  const handleCompanyChange = async (selectedOption: any) => {
    if (!selectedOption) return;

    const newFormData = {
      company_id: selectedOption.value,
      company_name:
        selectedOption.label.split("-")[0]?.trim() || "All Companies",
      company_type: selectedOption.label.split("-")[1]?.trim() || "Type",
    };

    localStorage.setItem("selectedAssetCompany", JSON.stringify(newFormData));
    setformDataCompanyChoose(newFormData);
  };

  // Transform companies for dropdown
  const companyOptions = companies.map((company: any) => ({
    value: company.id.toString(),
    label: `${company.name} - ${company.type}`,
    companyName: company.name,
  }));

  // Load companies on mount
  useEffect(() => {
    getAllListsAndData();
  }, []);

  // Handle company selection from localStorage
  useEffect(() => {
    if (companies.length) {
      const storedCompany = localStorage.getItem("selectedAssetCompany");
      if (storedCompany) {
        const parsed = JSON.parse(storedCompany);
        handleCompanyChange({
          value: parsed.company_id,
          label: `${parsed.company_name} - ${parsed.company_type}`,
        });
      } else {
        handleCompanyChange({
          value: companies[0].id.toString(),
          label: `${companies[0].name} - ${companies[0].type}`,
        });
      }
    }
  }, [companies]);

  // Update data when company changes
  useEffect(() => {
    updateUI();
  }, [formDataCompanyChoose]);

  // Apply filters to data
  useEffect(() => {
    let newFilteredData = [...data];

    if (statusFilter) {
      newFilteredData = newFilteredData.filter(
        (item) => item.status === statusFilter
      );
    }

    if (selectedStartDate && selectedEndDate) {
      newFilteredData = newFilteredData.filter((item) => {
        const createdAt = new Date(item.created_at);
        return (
          createdAt >= selectedStartDate.toDate() &&
          createdAt <= selectedEndDate.toDate()
        );
      });
    }

    setFilteredData(newFilteredData);
  }, [statusFilter, selectedStartDate, selectedEndDate, data]);

  const handleAddSubCategory = (parentRecord: any) => {
    setSelectedParentCategory({
      parent_category_id: parentRecord.id,
      parent_category_name: parentRecord.name,
    });
  };

  const handleCategoryDelete = (record: any) => {
    setDeleteRecord(record);
  };

  return (
    <>
      {isLoading && <Loader />}
      <div className="page-wrapper">
        <div className="content">
          {/* Page header with breadcrumb and actions */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">Asset Category</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.adminDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">Administration</li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Asset Category
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <div className="me-2 mb-2" title="Choose Company">
                <CommonSelect
                  className="select"
                  options={companyOptions}
                  defaultValue={{
                    value: formDataCompanyChoose.company_id,
                    label: `${truncateCompanyText(
                      formDataCompanyChoose.company_name,
                      20
                    )}-${formDataCompanyChoose.company_type}`,
                  }}
                  onChange={handleCompanyChange}
                />
              </div>
              <div className="mb-2 me-2">
                <Link
                  title="Add New Category"
                  to="#"
                  data-bs-toggle="modal"
                  data-bs-target="#add_category"
                  className="btn btn-primary d-flex align-items-center"
                >
                  <i className="ti ti-plus me-2" />
                  Add New Category
                </Link>
              </div>
              <div className="ms-2 head-icons">
                <CollapseHeader />
              </div>
            </div>
          </div>

          {/* Main content card */}
          <div className="card">
            <CategoryTable
              dataSource={filteredData}
              onEdit={handleCategoryEdit}
              onDelete={handleCategoryDelete}
              onAddSubCategory={handleAddSubCategory}
              selectedCompany={formDataCompanyChoose}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <p className="mb-0">2025 © CMS.</p>
          <p>
            Designed & Developed By{" "}
            <Link to="#" className="text-primary">
              ZYVO
            </Link>
          </p>
        </div>
      </div>

      {/* Edit category modal */}
      <EditCategory
        selectedCompany={formDataCompanyChoose}
        selectedCategory={selectedCategoryEdit}
        updateUI={updateUI}
      />

      {/* Modals */}
      <DeleteModal
        modalId="delete_modal"
        onDelete={handleDelete}
        title="Delete Item"
        message="Are you sure you want to delete this item? This action cannot be undone."
      />

      <AddCategory
        updateUI={updateUI}
        formDataCompanyChoose={formDataCompanyChoose}
      />

      <AddSubCategory
        selectedCompany={formDataCompanyChoose}
        parentCategory={selectedParentCategory}
        updateUI={updateUI}
      />
    </>
  );
};

export default AssetsCategory;
